
import React from "react";
import { motion } from "framer-motion";
import { getStatusColor, getHormoneStatus, getHormoneReference } from "@/utils/hormonalAnalysis";

export function HormonalFlowchart({ examValues, gender }) {
  const getNodeColor = (hormone) => {
    const value = examValues[hormone];
    const reference = getHormoneReference(hormone, gender);
    const status = getHormoneStatus(value, reference);
    return status === "normal" ? "#4ade80" : status === "high" ? "#ef4444" : "#f59e0b";
  };

  const getStatusText = (hormone) => {
    const value = examValues[hormone];
    const reference = getHormoneReference(hormone, gender);
    const status = getHormoneStatus(value, reference);
    return status === "normal" ? "Normal" : status === "high" ? "Elevado" : "Baixo";
  };

  const formatValue = (value, unit) => {
    return `${value?.toFixed(2) || "N/A"} ${unit}`;
  };

  return (
    <div className="space-y-6">
      <div className="p-4 rounded-lg bg-[#16213e]/50 border border-[#ff3366]/20">
        <h3 className="text-lg font-semibold text-[#ff3366] mb-2">
          Entendendo o Fluxograma
        </h3>
        <p className="text-white/80 text-sm">
          Este fluxograma representa a cascata hormonal do eixo hipotálamo-hipófise-gonadal. 
          As cores indicam o status de cada hormônio:
        </p>
        <ul className="mt-2 space-y-1 text-sm text-white/80">
          <li>🟢 Verde: Níveis normais</li>
          <li>🟡 Amarelo: Níveis alterados</li>
          <li>🔴 Vermelho: Níveis críticos</li>
        </ul>
      </div>

      <div className="relative h-[500px] w-full">
        {/* Hipotálamo */}
        <motion.div
          className="absolute top-0 left-1/2 transform -translate-x-1/2 p-6 rounded-lg bg-[#16213e] border border-[#ff3366]/20 w-64"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <h4 className="text-[#ff3366] font-semibold text-center">Hipotálamo</h4>
          <p className="text-sm text-white/80 text-center mt-1">GnRH</p>
          <p className="text-xs text-white/60 text-center mt-2">
            Controle central da função reprodutiva
          </p>
        </motion.div>

        {/* Hipófise */}
        <motion.div
          className="absolute top-40 left-1/2 transform -translate-x-1/2 p-6 rounded-lg w-72"
          style={{ backgroundColor: getNodeColor("lh") }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <h4 className="text-white font-semibold text-center">Hipófise Anterior</h4>
          <div className="mt-3 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-white/90">LH:</span>
              <span className="text-white/90">{formatValue(examValues.lh, "mUI/mL")}</span>
              <span className="text-xs text-white/80">({getStatusText("lh")})</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-white/90">FSH:</span>
              <span className="text-white/90">{formatValue(examValues.fsh, "mUI/mL")}</span>
              <span className="text-xs text-white/80">({getStatusText("fsh")})</span>
            </div>
          </div>
        </motion.div>

        {/* Gônadas */}
        <motion.div
          className="absolute top-80 left-1/2 transform -translate-x-1/2 p-6 rounded-lg w-80"
          style={{ backgroundColor: getNodeColor(gender === "Masculino" ? "testosterona" : "estradiol") }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <h4 className="text-white font-semibold text-center">
            {gender === "Masculino" ? "Testículos" : "Ovários"}
          </h4>
          <div className="mt-3 space-y-2">
            {gender === "Masculino" ? (
              <>
                <div className="flex justify-between items-center">
                  <span className="text-white/90">Testosterona:</span>
                  <span className="text-white/90">{formatValue(examValues.testosterona, "ng/dL")}</span>
                  <span className="text-xs text-white/80">({getStatusText("testosterona")})</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/90">Estradiol:</span>
                  <span className="text-white/90">{formatValue(examValues.estradiol, "pg/mL")}</span>
                  <span className="text-xs text-white/80">({getStatusText("estradiol")})</span>
                </div>
              </>
            ) : (
              <>
                <div className="flex justify-between items-center">
                  <span className="text-white/90">Estradiol:</span>
                  <span className="text-white/90">{formatValue(examValues.estradiol, "pg/mL")}</span>
                  <span className="text-xs text-white/80">({getStatusText("estradiol")})</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/90">Progesterona:</span>
                  <span className="text-white/90">{formatValue(examValues.progesterona, "ng/mL")}</span>
                  <span className="text-xs text-white/80">({getStatusText("progesterona")})</span>
                </div>
              </>
            )}
          </div>
        </motion.div>

        {/* Linhas de conexão */}
        <svg className="absolute inset-0 w-full h-full" style={{ zIndex: -1 }}>
          {/* Hipotálamo -> Hipófise */}
          <line
            x1="50%"
            y1="84"
            x2="50%"
            y2="160"
            stroke="#ff3366"
            strokeWidth="3"
            strokeDasharray="6"
          >
            <title>GnRH estimula a produção de LH e FSH</title>
          </line>
          {/* Hipófise -> Gônadas */}
          <line
            x1="50%"
            y1="244"
            x2="50%"
            y2="320"
            stroke="#ff3366"
            strokeWidth="3"
            strokeDasharray="6"
          >
            <title>LH e FSH estimulam as gônadas</title>
          </line>
        </svg>

        {/* Feedback arrows */}
        <svg className="absolute inset-0 w-full h-full" style={{ zIndex: -1 }}>
          <path
            d="M 100,400 C 50,300 50,100 100,50"
            fill="none"
            stroke="#ff3366"
            strokeWidth="2"
            strokeDasharray="4"
          >
            <title>Feedback negativo</title>
          </path>
          <polygon
            points="100,45 95,55 105,55"
            fill="#ff3366"
            transform="rotate(-30 100 50)"
          />
        </svg>

        {/* Legenda Detalhada */}
        <div className="absolute bottom-4 left-4 p-4 rounded-lg bg-[#16213e]/80 border border-[#ff3366]/20">
          <h4 className="text-sm font-semibold text-[#ff3366] mb-3">Interpretação</h4>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-green-500" />
              <span className="text-xs text-white/80">Normal: Dentro da faixa de referência</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-yellow-500" />
              <span className="text-xs text-white/80">Alterado: Requer atenção</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-red-500" />
              <span className="text-xs text-white/80">Crítico: Necessita intervenção</span>
            </div>
          </div>
        </div>

        {/* Valores de Referência */}
        <div className="absolute bottom-4 right-4 p-4 rounded-lg bg-[#16213e]/80 border border-[#ff3366]/20">
          <h4 className="text-sm font-semibold text-[#ff3366] mb-3">Valores de Referência</h4>
          <div className="space-y-1 text-xs text-white/80">
            {gender === "Masculino" ? (
              <>
                <p>Testosterona: 240-950 ng/dL</p>
                <p>Estradiol: 10-40 pg/mL</p>
                <p>LH: 1.5-9.3 mUI/mL</p>
                <p>FSH: 1.6-8.0 mUI/mL</p>
              </>
            ) : (
              <>
                <p>Estradiol: 30-400 pg/mL</p>
                <p>LH: 2.4-12.6 mUI/mL</p>
                <p>FSH: 3.5-12.5 mUI/mL</p>
                <p>Progesterona: 0.2-28 ng/mL</p>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 rounded-lg bg-[#16213e]/50 border border-[#ff3366]/20">
        <h3 className="text-lg font-semibold text-[#ff3366] mb-2">
          Análise do Eixo Hormonal
        </h3>
        <p className="text-white/80 text-sm">
          {gender === "Masculino" ? (
            <>
              O eixo hipotálamo-hipófise-testicular é responsável pela produção de testosterona
              e regulação da função reprodutiva masculina. O GnRH estimula a produção de LH e FSH,
              que por sua vez estimulam os testículos a produzir testosterona e espermatozoides.
            </>
          ) : (
            <>
              O eixo hipotálamo-hipófise-ovariano regula o ciclo menstrual e a função reprodutiva
              feminina. O GnRH estimula a produção de LH e FSH, que controlam a produção de
              estradiol e progesterona pelos ovários.
            </>
          )}
        </p>
      </div>
    </div>
  );
}
